import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import styled from "styled-components";
import { colors2, UI_BODY, UI_TAG, SwitchSingle } from "oolib";
import { Tag } from "oolib/dist/v2/components/Tags";
import { buildDatasetGraph, FIELD_TYPE_META } from "../utils/buildDatasetGraph";

const ROW_GAP = 60;
const COL_GAP = 36;
const KIND_COLOR = { content: "#1570EF", tag: "#9E77ED" };

/* --------------------------- layout containers ------------------------ */

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
`;

const StyledToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const StyledLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  align-items: center;
`;

const StyledScroller = styled.div`
  overflow: auto;
  max-height: 74vh;
  background: ${colors2.grey5};
  border-radius: 8px;
  padding: 28px;
`;

const StyledInner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${ROW_GAP}px;
  min-width: 100%;
  width: fit-content;
  margin: 0 auto;
`;

const StyledRowLayer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${COL_GAP}px;
  position: relative;
  z-index: 1;
`;

const StyledBand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  z-index: 1;
  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${colors2.grey20};
  }
`;

const StyledSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
`;

const StyledBox = styled.div`
  background: #fff;
  border: 1.5px solid
    ${({ $kind }) => ($kind === "tag" ? "#D6BBFB" : colors2.grey20)};
  border-top: 4px solid ${({ $kind }) => KIND_COLOR[$kind] || KIND_COLOR.content};
  border-radius: 10px;
  box-shadow: ${({ $pinned, $active }) =>
    $pinned
      ? "0 0 0 2.5px #7F56D9, 0 6px 16px rgba(127,86,217,0.22)"
      : $active
      ? "0 0 0 2px #9E77ED"
      : "0 1px 2px rgba(16,24,40,0.08)"};
  opacity: ${({ $dim }) => ($dim ? 0.3 : 1)};
  transition: opacity 0.12s ease, box-shadow 0.12s ease;
  overflow: hidden;
  cursor: pointer;
  align-self: flex-start;
  width: ${({ $detailed }) => ($detailed ? "248px" : "auto")};
  min-width: 150px;
`;

const StyledBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ $kind }) => ($kind === "tag" ? "#F9F5FF" : "#F5FAFF")};
`;

const StyledBoxTitle = styled(UI_BODY)`
  font-weight: 600;
  white-space: nowrap;
`;

const StyledFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-top: 1px solid ${colors2.grey10};
`;

const StyledEmpty = styled.div`
  padding: 48px;
  text-align: center;
  color: ${colors2.grey50};
`;

/* -------------------------------- layout ------------------------------ */

// Datasets occupy the upper levels, tag collections the lower levels — each
// banded separately and ordered (barycentre) to keep arrows untangled.
const layoutVertical = (nodes, edges) => {
  const byId = {};
  nodes.forEach((n) => (byId[n.id] = n));
  const isContent = (id) => byId[id]?.kind !== "tag";

  const dOut = {};
  const tOut = {};
  const allIn = {};
  const allOut = {};
  nodes.forEach((n) => {
    dOut[n.id] = [];
    tOut[n.id] = [];
    allIn[n.id] = [];
    allOut[n.id] = [];
  });
  edges.forEach((e) => {
    if (!byId[e.fromNodeId] || !byId[e.toNodeId]) return;
    allOut[e.fromNodeId].push(e.toNodeId);
    allIn[e.toNodeId].push(e.fromNodeId);
    const fc = isContent(e.fromNodeId);
    const tc = isContent(e.toNodeId);
    if (fc && tc) dOut[e.fromNodeId].push(e.toNodeId);
    if (!fc && !tc) tOut[e.fromNodeId].push(e.toNodeId);
  });

  const longestDepth = (outMap) => {
    const depth = {};
    const vis = new Set();
    const calc = (id) => {
      if (depth[id] !== undefined) return depth[id];
      if (vis.has(id)) return 0;
      vis.add(id);
      let d = 0;
      for (const s of outMap[id] || []) d = Math.max(d, calc(s) + 1);
      vis.delete(id);
      depth[id] = d;
      return d;
    };
    nodes.forEach((n) => calc(n.id));
    return depth;
  };
  const dDepth = longestDepth(dOut);
  const tDepth = longestDepth(tOut);

  const datasetIds = nodes.filter((n) => isContent(n.id)).map((n) => n.id);
  const tagIds = nodes.filter((n) => !isContent(n.id)).map((n) => n.id);
  const Dmax = Math.max(0, ...datasetIds.map((id) => dDepth[id]));
  const Tmax = Math.max(0, ...tagIds.map((id) => tDepth[id]));
  const datasetBand = datasetIds.length ? Dmax + 1 : 0;
  const totalRows = datasetBand + (tagIds.length ? Tmax + 1 : 0);

  const rowOf = (id) =>
    isContent(id) ? Dmax - dDepth[id] : datasetBand + (Tmax - tDepth[id]);

  const rows = Array.from({ length: totalRows }, () => []);
  nodes.forEach((n) => rows[rowOf(n.id)]?.push(n.id));
  rows.forEach((r) => r.sort((a, b) => byId[a].title.localeCompare(byId[b].title)));

  const idx = {};
  rows.forEach((r) => r.forEach((id, i) => (idx[id] = i)));
  const bary = (id, ne) =>
    ne.length ? ne.reduce((s, n) => s + (idx[n] ?? 0), 0) / ne.length : idx[id];
  for (let p = 0; p < 4; p++) {
    const down = p % 2 === 0;
    const order = down ? rows.map((_, i) => i) : rows.map((_, i) => i).reverse();
    for (const ri of order) {
      const ne = (id) => (down ? allIn[id] : allOut[id]);
      rows[ri] = rows[ri].slice().sort((a, b) => bary(a, ne(a)) - bary(b, ne(b)));
      rows[ri].forEach((id, i) => (idx[id] = i));
    }
  }

  return { rows, byId, firstTagRow: tagIds.length ? datasetBand : -1 };
};

const buildEdgePath = (x1, y1, x2, y2) => {
  const dy = Math.max(28, (y2 - y1) / 2);
  return `M ${x1} ${y1} C ${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
};

/* ------------------------------ component ----------------------------- */

export const ConnectionsView = ({ allTpls = [] }) => {
  const [detailed, setDetailed] = useState(false);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(() => new Set());

  const { nodes, edges } = useMemo(() => buildDatasetGraph(allTpls), [allTpls]);
  const { rows, byId, firstTagRow } = useMemo(
    () => layoutVertical(nodes, edges),
    [nodes, edges]
  );

  const innerRef = useRef(null);
  const boxRefs = useRef({});
  const [paths, setPaths] = useState([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const inner = innerRef.current;
      if (!inner) return;
      const base = inner.getBoundingClientRect();
      const next = [];
      for (const e of edges) {
        const childEl = boxRefs.current[e.fromNodeId];
        const parentEl = boxRefs.current[e.toNodeId];
        if (!childEl || !parentEl) continue;
        const c = childEl.getBoundingClientRect();
        const p = parentEl.getBoundingClientRect();
        const x1 = c.left + c.width / 2 - base.left;
        const y1 = c.bottom - base.top;
        const x2 = p.left + p.width / 2 - base.left;
        const y2 = p.top - base.top;
        next.push({
          id: e.id,
          from: e.fromNodeId,
          to: e.toNodeId,
          d: buildEdgePath(x1, y1, x2, y2),
          label: e.label !== byId[e.toNodeId]?.title ? e.label : null,
          lx: (x1 + x2) / 2,
          ly: (y1 + y2) / 2,
        });
      }
      setPaths(next);
      setDims({ w: inner.scrollWidth, h: inner.scrollHeight });
    };
    const raf = requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    if (innerRef.current) ro.observe(innerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [nodes, edges, detailed, rows]);

  // Highlight the union of every active box (all pinned boxes + the hovered
  // one) and their direct connections. Multiple boxes can be pinned at once.
  const activeIds = useMemo(() => {
    const s = new Set(pinned);
    if (hover) s.add(hover);
    return s;
  }, [pinned, hover]);

  const highlightNodes = useMemo(() => {
    if (!activeIds.size) return null;
    const set = new Set(activeIds);
    edges.forEach((e) => {
      if (activeIds.has(e.fromNodeId)) set.add(e.toNodeId);
      if (activeIds.has(e.toNodeId)) set.add(e.fromNodeId);
    });
    return set;
  }, [activeIds, edges]);
  const isEdgeActive = (from, to) => activeIds.has(from) || activeIds.has(to);

  // The specific tag field(s) that create the active connections, so they can
  // be emphasised inside their box (using only the Tag component's own props).
  const activeFieldKeys = useMemo(() => {
    if (!activeIds.size) return null;
    const s = new Set();
    edges.forEach((e) => {
      if (activeIds.has(e.fromNodeId) || activeIds.has(e.toNodeId)) {
        s.add(`${e.fromNodeId}::${e.fromFieldKey}`);
      }
    });
    return s;
  }, [activeIds, edges]);

  const detailedOption = { value: "detailed", display: "Show field details" };

  if (!nodes.length) {
    return (
      <StyledRoot>
        <StyledEmpty>
          <UI_BODY>No datasets found for this tenant.</UI_BODY>
        </StyledEmpty>
      </StyledRoot>
    );
  }

  return (
    <StyledRoot>
      <StyledToolbar>
        <UI_TAG style={{ color: pinned.size ? "#6941C6" : colors2.grey60 }}>
          {pinned.size
            ? `Pinned (${pinned.size}): ${[...pinned].map((id) => byId[id]?.title).join(", ")} — click a pinned box to unpin, or the background to clear all.`
            : `Dataset connections — hover to highlight, click to pin (you can pin several) · ${nodes.length} datasets, ${edges.length} links`}
        </UI_TAG>
        <SwitchSingle
          id="connections-detail-switch"
          option={detailedOption}
          value={detailed ? detailedOption : undefined}
          onChange={(id, next) => setDetailed(Boolean(next))}
          layoutStyle="style2"
        />
      </StyledToolbar>

      <StyledLegend>
        <Tag display="Content dataset" badgeColor={KIND_COLOR.content} size="XS" variant="clear" />
        <Tag display="Tag collection" badgeColor={KIND_COLOR.tag} size="XS" variant="clear" />
        {detailed &&
          Object.entries(FIELD_TYPE_META).map(([type, meta]) => (
            <Tag key={type} display={meta.label} badgeColor={meta.color} size="XS" variant="clear" />
          ))}
      </StyledLegend>

      <StyledScroller onClick={() => setPinned(new Set())}>
        <StyledInner ref={innerRef}>
          <StyledSvg width={dims.w} height={dims.h}>
            <defs>
              <marker id="cv-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#98A2B3" />
              </marker>
              <marker id="cv-arrow-on" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#7F56D9" />
              </marker>
            </defs>
            {paths.map((p) => {
              const active = isEdgeActive(p.from, p.to);
              const dim = highlightNodes && !active;
              return (
                <g key={p.id}>
                  <path
                    d={p.d}
                    fill="none"
                    stroke={active ? "#7F56D9" : "#98A2B3"}
                    strokeWidth={active ? 2.5 : 1.5}
                    strokeOpacity={dim ? 0.1 : 0.85}
                    markerEnd={`url(#${active ? "cv-arrow-on" : "cv-arrow"})`}
                  />
                  {p.label && (!highlightNodes || active) && (
                    <text x={p.lx} y={p.ly} fontSize="9.5" fill={colors2.grey50} textAnchor="middle" style={{ paintOrder: "stroke" }} stroke="#F2F4F7" strokeWidth="3">
                      {p.label}
                    </text>
                  )}
                </g>
              );
            })}
          </StyledSvg>

          {rows.map((rowIds, ri) => (
            <React.Fragment key={ri}>
              {ri === 0 && firstTagRow !== 0 && (
                <StyledBand>
                  <UI_TAG style={{ color: colors2.grey50 }}>Datasets</UI_TAG>
                </StyledBand>
              )}
              {ri === firstTagRow && (
                <StyledBand>
                  <UI_TAG style={{ color: colors2.grey50 }}>
                    Tag collections — what the datasets roll up into
                  </UI_TAG>
                </StyledBand>
              )}
              <StyledRowLayer>
                {rowIds.map((id) => {
                  const node = byId[id];
                  if (!node) return null;
                  const dim = highlightNodes && !highlightNodes.has(id);
                  const isPinned = pinned.has(id);
                  const active = hover === id || isPinned;
                  return (
                    <StyledBox
                      key={id}
                      ref={(el) => {
                        boxRefs.current[id] = el;
                      }}
                      $kind={node.kind}
                      $dim={dim}
                      $active={active}
                      $pinned={isPinned}
                      $detailed={detailed}
                      onMouseEnter={() => setHover(id)}
                      onMouseLeave={() => setHover(null)}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setPinned((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        });
                      }}
                    >
                      <StyledBoxHeader $kind={node.kind}>
                        {isPinned && (
                          <span style={{ fontSize: 11 }} title="Pinned">📌</span>
                        )}
                        <StyledBoxTitle>{node.title}</StyledBoxTitle>
                        <span style={{ marginLeft: "auto" }}>
                          <Tag
                            display={node.kind === "tag" ? "Tag" : "Dataset"}
                            badgeColor={KIND_COLOR[node.kind]}
                            size="XS"
                            variant="clear"
                          />
                        </span>
                      </StyledBoxHeader>
                      {detailed && node.fields.length > 0 && (
                        <StyledFields>
                          {node.fields.map((f) => {
                            const fieldActive = activeFieldKeys?.has(
                              `${node.id}::${f.key}`
                            );
                            return (
                              <Tag
                                key={f.key}
                                display={f.label}
                                badgeColor={
                                  (FIELD_TYPE_META[f.type] || FIELD_TYPE_META.text).color
                                }
                                size={fieldActive ? "S" : "XS"}
                                variant="default"
                                fill={fieldActive ? "solid" : "outline"}
                                weight={fieldActive ? "bold" : undefined}
                                style={
                                  fieldActive
                                    ? {
                                        background: colors2.greenBG,
                                        outline: `1px solid ${colors2.greenStroke}`,
                                      }
                                    : undefined
                                }
                              />
                            );
                          })}
                        </StyledFields>
                      )}
                    </StyledBox>
                  );
                })}
              </StyledRowLayer>
            </React.Fragment>
          ))}
        </StyledInner>
      </StyledScroller>
    </StyledRoot>
  );
};
